/**
 * Hydrated suburb cache — a singleton that lifts the per-tenant suburb_cache
 * table into indexed in-memory maps via a Web Worker.
 *
 * Why this exists:
 *   The Phase 5 read API in `lib/suburbs.ts` round-trips to Supabase on every
 *   suburb / postcode lookup. That's fine at idle, but `findRegionForAddress`
 *   is called once per deal during the dashboard hydration — at 500 deals
 *   that's 500 queries before pins paint. This module fetches every row
 *   once, hands them to a worker for indexing + centroid math, and exposes
 *   synchronous accessors that callers can short-circuit through before
 *   falling back to the network.
 *
 * Lifecycle:
 *   - First call to `loadSuburbCache()` triggers a single fetch + worker
 *     handoff. Subsequent callers await the in-flight promise.
 *   - `getCached*` functions return `undefined` until hydration completes.
 *     Callers must handle the un-hydrated case (the existing Supabase
 *     paths in `lib/suburbs.ts` and `regionValidation.ts` are the fallback).
 *   - Call `invalidateSuburbCache()` after a Sheets resync so the next
 *     access re-hydrates.
 */
import { getSupabase } from './supabase';
import SuburbCacheWorker from '../workers/suburbCache.worker.ts?worker';

interface RawRow {
  suburb: string;
  postcode: string | null;
  state: string | null;
  zone: string | null;
  verdict_label: string | null;
  raw_row: Record<string, unknown> | null;
}

interface ZoneCentroid {
  zone: string;
  lat: number;
  lng: number;
}

interface HydratedSuburbCache {
  byName: Record<string, RawRow>;
  byPostcode: Record<string, RawRow[]>;
  centroids: ZoneCentroid[];
}

let _cache: HydratedSuburbCache | null = null;
let _inflight: Promise<HydratedSuburbCache | null> | null = null;

export function getCachedZoneCentroids(): ZoneCentroid[] | undefined {
  return _cache?.centroids;
}

export function getCachedSuburbByName(name: string): RawRow | undefined {
  if (!_cache) return undefined;
  return _cache.byName[normaliseSuburbName(name)];
}

export function getCachedSuburbsByPostcode(postcode: string): RawRow[] | undefined {
  if (!_cache) return undefined;
  return _cache.byPostcode[postcode.trim()];
}

export function invalidateSuburbCache(): void {
  _cache = null;
  _inflight = null;
}

/**
 * Kick off hydration. Safe to call repeatedly — the second call onwards
 * returns the in-flight promise (or the cached result).
 *
 * Returns `null` if the cache is empty or hydration failed; callers should
 * treat that as "fall back to the per-row Supabase path".
 */
export function loadSuburbCache(): Promise<HydratedSuburbCache | null> {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;

  _inflight = (async () => {
    let rows: RawRow[] = [];
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('v9_suburb_cache')
        .select('suburb, postcode, state, zone, verdict_label, raw_row');
      if (error) {
        console.warn('[suburbCache] fetch failed:', error.message);
        return null;
      }
      rows = (data ?? []) as RawRow[];
    } catch (err) {
      console.warn('[suburbCache] fetch threw:', err);
      return null;
    }

    if (rows.length === 0) {
      _cache = { byName: {}, byPostcode: {}, centroids: [] };
      return _cache;
    }

    // Skip the worker on tiny datasets — the postMessage round-trip costs
    // more than the work itself below ~200 rows.
    if (rows.length < 200) {
      _cache = hydrateOnMainThread(rows);
      return _cache;
    }

    try {
      _cache = await hydrateInWorker(rows);
      return _cache;
    } catch (err) {
      console.warn('[suburbCache] worker failed, falling back to main thread:', err);
      _cache = hydrateOnMainThread(rows);
      return _cache;
    }
  })();

  return _inflight;
}

function hydrateInWorker(rows: RawRow[]): Promise<HydratedSuburbCache> {
  return new Promise((resolve, reject) => {
    const worker = new SuburbCacheWorker();
    const cleanup = () => {
      worker.terminate();
    };
    worker.addEventListener(
      'message',
      (e: MessageEvent) => {
        const data = e.data as
          | ({ type: 'ok' } & HydratedSuburbCache)
          | { type: 'error'; message: string };
        cleanup();
        if (data?.type === 'ok') {
          resolve({ byName: data.byName, byPostcode: data.byPostcode, centroids: data.centroids });
        } else {
          reject(new Error(data?.type === 'error' ? data.message : 'worker returned unexpected message'));
        }
      },
      { once: true },
    );
    worker.addEventListener(
      'error',
      (ev: ErrorEvent) => {
        cleanup();
        reject(new Error(ev.message || 'worker error'));
      },
      { once: true },
    );
    worker.postMessage({ type: 'hydrate', rows });
  });
}

// Mirror of the worker's logic so the main-thread fallback returns the same
// shape. Keep these two in sync.
function hydrateOnMainThread(rows: RawRow[]): HydratedSuburbCache {
  const byName: Record<string, RawRow> = {};
  const byPostcode: Record<string, RawRow[]> = {};
  const buckets = new Map<string, { lat: number; lng: number; n: number }>();

  for (const row of rows) {
    if (!row || !row.suburb) continue;
    const key = normaliseSuburbName(row.suburb);
    if (!byName[key] || (row.postcode && !byName[key].postcode)) byName[key] = row;
    if (row.postcode) {
      const pc = row.postcode.trim();
      (byPostcode[pc] ||= []).push(row);
    }
    if (row.zone) {
      const lat = extractNumber(row.raw_row, ['lat', 'latitude', 'centroid_lat', 'Lat', 'Latitude']);
      const lng = extractNumber(row.raw_row, ['lng', 'longitude', 'centroid_lng', 'Lng', 'Longitude', 'Long']);
      if (lat != null && lng != null) {
        const cur = buckets.get(row.zone) ?? { lat: 0, lng: 0, n: 0 };
        cur.lat += lat;
        cur.lng += lng;
        cur.n += 1;
        buckets.set(row.zone, cur);
      }
    }
  }

  const centroids: ZoneCentroid[] = [];
  for (const [zone, agg] of buckets.entries()) {
    if (agg.n === 0) continue;
    centroids.push({ zone, lat: agg.lat / agg.n, lng: agg.lng / agg.n });
  }
  return { byName, byPostcode, centroids };
}

function normaliseSuburbName(s: string): string {
  return String(s || '')
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractNumber(obj: Record<string, unknown> | null | undefined, keys: string[]): number | null {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = parseFloat(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}
